import { Badge } from "@/components/ui/badge";
import { useDeployments } from "@/lib/hooks/useDeployments";
import { formatDateISO } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { Cloud } from "lucide-react";
import type { DeploymentSummary } from "../../../schemas/codegen";

interface CompactDeploysContentProps {
  projectId: string;
}

export function CompactDeploysContent({
  projectId,
}: CompactDeploysContentProps) {
  const navigate = useNavigate();
  const { data: deploymentsData, isLoading } = useDeployments(projectId);
  const deployments = deploymentsData?.deployments || [];

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "complete":
        return "default" as const;
      case "error":
      case "cancelled":
        return "destructive" as const;
      case "executing":
        return "default" as const;
      case "pending":
        return "secondary" as const;
      default:
        return "outline" as const;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {["skeleton-1", "skeleton-2", "skeleton-3"].map((key) => (
          <div key={key} className="animate-pulse">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-muted rounded" />
                <div className="w-20 h-4 bg-muted rounded" />
              </div>
              <div className="w-16 h-5 bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (deployments.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        <div className="text-center">
          <Cloud className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No deployments yet</p>
          <p className="text-xs">Deploy your first app to see it here</p>
        </div>
      </div>
    );
  }

  // Show only the 5 most recent deployments
  const recentDeployments = deployments
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .slice(0, 5);

  return (
    <div className="space-y-3">
      {recentDeployments.map((deployment: DeploymentSummary) => (
        <button
          key={deployment.id}
          type="button"
          onClick={() =>
            navigate({
              to: "/projects/$projectId",
              params: { projectId },
              search: (prev) => ({
                ...prev,
                tab: "deploys",
                deploymentId: deployment.id,
              }),
            })
          }
          className="border rounded-lg p-3 hover:bg-muted/50 transition-colors w-full text-left"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded truncate">
                {deployment.id.slice(0, 8)}...
              </code>
            </div>
            <Badge
              variant={getStatusBadgeVariant(deployment.status)}
              className="text-xs"
            >
              {deployment.status}
            </Badge>
          </div>

          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>Updated:</span>
              <span>{formatDateISO(deployment.updatedAt)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Version:</span>
              <code className="font-mono bg-muted px-1 py-0.5 rounded">
                {deployment.versionId.slice(0, 6)}...
              </code>
            </div>
          </div>
        </button>
      ))}

      {deployments.length > 5 && (
        <div className="text-center pt-2">
          <p className="text-xs text-muted-foreground">
            +{deployments.length - 5} more deployments
          </p>
        </div>
      )}
    </div>
  );
}
